// Function to initialize video section (jarallax and video popup)
export function initializeVideoSection(container?: HTMLElement | null, bgElement?: HTMLDivElement | null) {
    // Initialize jarallax for background parallax effect
    if (bgElement) {
        // Determine the background image URL either from data attribute or inline styles
        const extractInlineImage = (): string | null => {
            const inlineStyle = bgElement.style.backgroundImage;
            if (inlineStyle && inlineStyle !== 'none') {
                const match = inlineStyle.match(/url\(["']?(.*?)["']?\)/);
                if (match && match[1]) {
                    return match[1];
                }
            }
            return null;
        };

        // Get image URL with fallback support (Issue #1 & #2)
        const imgUrl =
            bgElement.getAttribute('data-jarallax-img') ||
            bgElement.getAttribute('data-jarallax-img-fallback') ||
            extractInlineImage() ||
            bgElement
                .getAttribute('data-jarallax-original-styles')
                ?.match(/background-image:\s*url\(["']?(.*?)["']?\)/)?.[1] ||
            null;

        // Ensure the element has proper dimensions first
        const parent = bgElement.parentElement;
        if (parent) {
            const parentHeight = parent.offsetHeight || parent.clientHeight;
            if (parentHeight === 0) {
                // Wait for parent to have height
                setTimeout(() => initializeVideoSection(container, bgElement), 100);
                return;
            }
        }

        const jarallax = (window as any).jarallax;
        if (typeof jarallax !== 'undefined' && typeof jarallax === 'function') {
            try {
                // Destroy existing jarallax instance if any
                if ((bgElement as any).jarallax) {
                    jarallax(bgElement, 'destroy');
                }

                // Ensure image URL is set before jarallax (Issue #2 - jarallax overriding)
                if (imgUrl) {
                    bgElement.setAttribute('data-jarallax-img', imgUrl);
                    // Force set background image to prevent jarallax from clearing it
                    bgElement.style.backgroundImage = `url(${imgUrl})`;
                    bgElement.style.backgroundSize = 'cover';
                    bgElement.style.backgroundPosition = '50% 0%';
                    bgElement.style.backgroundRepeat = 'no-repeat';
                }
                
                // Initialize jarallax with the image URL explicitly
                jarallax(bgElement, {
                    speed: 0.2,
                    imgPosition: '50% 0%',
                    imgSrc: imgUrl || undefined // Explicitly pass image source
                });
                
                // Double-check after initialization that image is still set (Issue #2)
                setTimeout(() => {
                    if (bgElement && imgUrl) {
                        const jarallaxImg = bgElement.querySelector('.jarallax-img') as HTMLImageElement;
                        if (jarallaxImg && !jarallaxImg.src) {
                            jarallaxImg.src = imgUrl;
                        }
                        // Also ensure the background style is preserved
                        if (!bgElement.style.backgroundImage || bgElement.style.backgroundImage === 'none') {
                            bgElement.style.backgroundImage = `url(${imgUrl})`;
                        }
                    }
                }, 100);
            } catch (error) {
                // Fallback: ensure background is visible even without jarallax
                bgElement.style.display = 'block';
                bgElement.style.visibility = 'visible';
                if (imgUrl) {
                    bgElement.style.backgroundImage = `url(${imgUrl})`;
                    bgElement.style.backgroundSize = 'cover';
                    bgElement.style.backgroundPosition = '50% 0%';
                    bgElement.style.backgroundRepeat = 'no-repeat';
                }
            }
        } else {
            // Retry if jarallax not loaded yet
            setTimeout(() => initializeVideoSection(container, bgElement), 500);
            return;
        }
    }

    // Initialize video popup
    const videoPopupElements = container 
        ? container.querySelectorAll('.video-popup')
        : document.querySelectorAll('.video-popup');
    
    if (videoPopupElements.length > 0) {
        const jQuery = (window as any).jQuery;
        if (typeof jQuery !== 'undefined' && typeof jQuery.fn !== 'undefined' && typeof jQuery.fn.magnificPopup !== 'undefined') {
            try {
                // Destroy existing popup instances first
                jQuery('.video-popup').each(function(this: HTMLElement) {
                    const $this = jQuery(this);
                    if ($this.data('magnificPopup')) {
                        $this.magnificPopup('close');
                        $this.magnificPopup('destroy');
                    }
                });
                
                // Initialize new popup with mobile-friendly settings
                jQuery('.video-popup').magnificPopup({
                    type: 'iframe',
                    mainClass: 'mfp-fade',
                    removalDelay: 160,
                    preloader: true,
                    fixedContentPos: false,
                    iframe: {
                        patterns: {
                            youtube: {
                                index: 'youtube.com/',
                                id: 'v=',
                                src: 'https://www.youtube.com/embed/%id%?autoplay=1'
                            },
                            vimeo: {
                                index: 'vimeo.com/',
                                id: '/',
                                src: 'https://player.vimeo.com/video/%id%?autoplay=1'
                            }
                        }
                    },
                    callbacks: {
                        open: function() {
                            // Disable scrolling when popup is open
                            document.body.classList.add('mfp-body-lock');
                            document.documentElement.classList.add('mfp-body-lock');
                        },
                        close: function() {
                            // Re-enable scrolling when popup is closed
                            document.body.classList.remove('mfp-body-lock');
                            document.documentElement.classList.remove('mfp-body-lock');
                        }
                    }
                });
            } catch (error) {
            }
        } else {
            // Retry if jQuery or magnificPopup not loaded yet
            setTimeout(() => initializeVideoSection(container, bgElement), 500);
        }
    }
}

/**
 * Initialize video popup for video section (similar to About section)
 * Creates a nice centered dialog with autoplay
 */
export function initializeVideoSectionPopup(containerElement: HTMLElement | null): void {
    if (!containerElement) {
        return;
    }

    setTimeout(() => {
        try {
            const videoLink = containerElement.querySelector('.video-one__video-link .video-popup') as HTMLAnchorElement;
            
            if (!videoLink) {
                return;
            }

            const videoUrl = videoLink.getAttribute('data-video-url') || videoLink.href;

            // Check if it's a YouTube URL
            const isYouTubeUrl = videoUrl && (
                videoUrl.includes('youtube.com') || 
                videoUrl.includes('youtu.be')
            );

            // Always use Magnific Popup for all videos (YouTube modal)
                const jQuery = (window as any).jQuery;
                if (typeof jQuery !== 'undefined' && jQuery.fn && jQuery.fn.magnificPopup) {
                // Convert to YouTube URL if not already YouTube
                let youtubeUrl = videoUrl;
                
                if (!isYouTubeUrl) {
                    // Use default YouTube video for non-YouTube URLs
                    youtubeUrl = 'https://www.youtube.com/watch?v=CR9Y3Do-aX8';
                }
                
                // Extract video ID from YouTube URL
                let videoId = '';
                const patterns = [
                    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
                    /[?&]v=([a-zA-Z0-9_-]+)/
                ];
                
                for (const pattern of patterns) {
                    const match = youtubeUrl.match(pattern);
                    if (match && match[1]) {
                        videoId = match[1].split('&')[0].split('?')[0].split('#')[0];
                        break;
                    }
                }
                
                // Store video ID in data attribute and use hash href to prevent navigation
                const finalVideoId = videoId || 'CR9Y3Do-aX8';
                videoLink.setAttribute('data-youtube-id', finalVideoId);
                videoLink.href = '#'; // Use hash to prevent any navigation
                
                
                // Store scroll position
                let scrollPosition = 0;
                
                // Get the embed URL
                const embedUrl = `https://www.youtube.com/embed/${finalVideoId}?autoplay=1&mute=1&rel=0&enablejsapi=1&origin=${window.location.origin}`;
                
                // Prevent default link behavior and lock scroll position IMMEDIATELY
                videoLink.addEventListener('click', function(e) {
                    
                    // CRITICAL: Lock scroll FIRST, before preventDefault
                    const initialScroll = window.pageYOffset || document.documentElement.scrollTop;
                    scrollPosition = initialScroll;
                    
                    // Lock scroll IMMEDIATELY and synchronously
                    document.body.style.position = 'fixed';
                    document.body.style.top = `-${scrollPosition}px`;
                    document.body.style.width = '100%';
                    document.body.style.overflow = 'hidden';
                    document.documentElement.style.overflow = 'hidden';
                    document.documentElement.style.position = 'fixed';
                    document.documentElement.style.top = `-${scrollPosition}px`;
                    
                    // Check scroll position after lock
                    setTimeout(() => {
                        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                        if (currentScroll !== 0 && currentScroll !== scrollPosition) {
                        }
                    }, 10);
                    
                    // Prevent ALL default behaviors
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    
                    // Prevent any scroll events
                    let scrollEventCount = 0;
                    const preventScroll = (e: Event) => {
                        scrollEventCount++;
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    };
                    window.addEventListener('scroll', preventScroll, { passive: false, capture: true });
                    window.addEventListener('wheel', preventScroll, { passive: false, capture: true });
                    window.addEventListener('touchmove', preventScroll, { passive: false, capture: true });
                    
                    // Monitor scroll position continuously
                    const scrollMonitor = setInterval(() => {
                        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                        if (currentScroll !== 0 && currentScroll !== scrollPosition) {
                        }
                    }, 50);
                    
                    // Store the prevent functions to remove later
                    const removeScrollPrevention = () => {
                        clearInterval(scrollMonitor);
                        window.removeEventListener('scroll', preventScroll, { capture: true });
                        window.removeEventListener('wheel', preventScroll, { capture: true });
                        window.removeEventListener('touchmove', preventScroll, { capture: true });
                    };
                    
                    // Open popup with direct URL
                    jQuery.magnificPopup.open({
                        items: {
                            src: embedUrl,
                            type: 'iframe'
                        },
                        mainClass: 'mfp-fade',
                        removalDelay: 160,
                        preloader: false,
                        fixedContentPos: true,
                        iframe: {
                            patterns: {
                                youtube: {
                                    index: 'youtube.com/',
                                    id: finalVideoId,
                                    src: embedUrl
                                }
                            }
                        },
                        callbacks: {
                            beforeOpen: function() {
                                const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                                
                                // Double-check scroll is locked (should already be from click)
                                if (scrollPosition === 0) {
                                    scrollPosition = currentScroll;
                                }
                                
                                // Force lock again
                                document.body.style.position = 'fixed';
                                document.body.style.top = `-${scrollPosition}px`;
                                document.body.style.width = '100%';
                                document.body.style.overflow = 'hidden';
                                document.documentElement.style.position = 'fixed';
                                document.documentElement.style.top = `-${scrollPosition}px`;
                                document.documentElement.style.overflow = 'hidden';
                                
                                // Check again after a moment
                                setTimeout(() => {
                                    const checkScroll = window.pageYOffset || document.documentElement.scrollTop;
                                    if (checkScroll !== 0 && checkScroll !== scrollPosition) {
                                    }
                                }, 100);
                            },
                            open: function() {
                                document.body.classList.add('mfp-body-lock');
                                document.documentElement.classList.add('mfp-body-lock');
                                
                                setTimeout(() => {
                                    const checkScroll = window.pageYOffset || document.documentElement.scrollTop;
                                    if (checkScroll !== 0 && checkScroll !== scrollPosition) {
                                    }
                                }, 100);
                            },
                            afterOpen: function() {
                                // Ensure video starts playing immediately and unmute
                                const iframe = jQuery('.mfp-iframe')[0] as HTMLIFrameElement;
                                if (iframe && iframe.contentWindow) {
                                    const playVideo = () => {
                                        try {
                                            iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                                            setTimeout(() => {
                                                iframe.contentWindow?.postMessage('{"event":"command","func":"unMute","args":""}', '*');
                                            }, 500);
                                        } catch (e) {
                                        }
                                    };
                                    playVideo();
                                    setTimeout(playVideo, 100);
                                    setTimeout(playVideo, 500);
                                }
                            },
                            close: function() {
                                // Remove scroll prevention
                                removeScrollPrevention();
                                
                                document.body.classList.remove('mfp-body-lock');
                                document.documentElement.classList.remove('mfp-body-lock');
                                document.body.style.position = '';
                                document.body.style.top = '';
                                document.body.style.width = '';
                                document.body.style.overflow = '';
                                document.documentElement.style.position = '';
                                document.documentElement.style.top = '';
                                document.documentElement.style.overflow = '';
                                
                                requestAnimationFrame(() => {
                                    window.scrollTo(0, scrollPosition);
                                    scrollPosition = 0;
                                });
                            }
                        }
                    });
                    
                    return false;
                }, true);

                    // Add touch event support for mobile
                    videoLink.addEventListener('touchstart', function(e) {
                        e.stopPropagation();
                        // Lock scroll immediately on touch
                        scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                        document.body.style.position = 'fixed';
                        document.body.style.top = `-${scrollPosition}px`;
                        document.body.style.width = '100%';
                        document.body.style.overflow = 'hidden';
                        document.documentElement.style.overflow = 'hidden';
                    }, { passive: false });
                    videoLink.addEventListener('touchend', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        // Ensure scroll is locked
                        scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                        document.body.style.position = 'fixed';
                        document.body.style.top = `-${scrollPosition}px`;
                        document.body.style.width = '100%';
                        document.body.style.overflow = 'hidden';
                        document.documentElement.style.overflow = 'hidden';
                        // Open popup with direct URL
                        setTimeout(() => {
                            jQuery.magnificPopup.open({
                                items: {
                                    src: embedUrl,
                                    type: 'iframe'
                                },
                                mainClass: 'mfp-fade',
                                removalDelay: 160,
                                preloader: false,
                                fixedContentPos: true,
                                iframe: {
                                    patterns: {
                                        youtube: {
                                            index: 'youtube.com/',
                                            id: finalVideoId,
                                            src: embedUrl
                                        }
                                    }
                                },
                                callbacks: {
                                    beforeOpen: function() {
                                        if (scrollPosition === 0) {
                                            scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                                        }
                                        document.body.style.position = 'fixed';
                                        document.body.style.top = `-${scrollPosition}px`;
                                        document.body.style.width = '100%';
                                        document.body.style.overflow = 'hidden';
                                        document.documentElement.style.overflow = 'hidden';
                                    },
                                    open: function() {
                                        document.body.classList.add('mfp-body-lock');
                                        document.documentElement.classList.add('mfp-body-lock');
                                    },
                                    afterOpen: function() {
                                        const iframe = jQuery('.mfp-iframe')[0] as HTMLIFrameElement;
                                        if (iframe && iframe.contentWindow) {
                                            const playVideo = () => {
                                                try {
                                                    iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                                                    setTimeout(() => {
                                                        iframe.contentWindow?.postMessage('{"event":"command","func":"unMute","args":""}', '*');
                                                    }, 500);
                                                } catch (e) {
                                                }
                                            };
                                            playVideo();
                                            setTimeout(playVideo, 100);
                                            setTimeout(playVideo, 500);
                                        }
                                    },
                                    close: function() {
                                        document.body.classList.remove('mfp-body-lock');
                                        document.documentElement.classList.remove('mfp-body-lock');
                                        document.body.style.position = '';
                                        document.body.style.top = '';
                                        document.body.style.width = '';
                                        document.body.style.overflow = '';
                                        document.documentElement.style.overflow = '';
                                        requestAnimationFrame(() => {
                                            window.scrollTo(0, scrollPosition);
                                            scrollPosition = 0;
                                        });
                                    }
                                }
                            });
                        }, 10);
                    }, { passive: false });

                } else {
            }
        } catch (error) {
        }
    }, 500);
}

// Expose function globally for use in other scripts
if (typeof window !== 'undefined') {
    (window as any).initializeVideoSection = initializeVideoSection;
    (window as any).initializeVideoSectionPopup = initializeVideoSectionPopup;
}

